/**
 *
 * @module certificateStorage
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';

const Storage = Services.Storage;
const DrmType = Services.Media.DrmType;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class CertificateStorage
 * @desc Provides a mechanism for storing a certificate.
 * @since 3.2.0
 *
 */
export default class CertificateStorage {

    /**
     *
     * @param {String} certificateKey
     * @param {String} clientId
     * @param {String} environment
     * @param {SDK.Services.Storage} [storage=null]
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(certificateKey, clientId, environment, storage = null, logger = Logging) {

        const errorMessage = {
            ctor: 'CertificateStorage(certificateKey, clientId, environment, storage, logger)',
            get clientId() {
                return `${this.ctor} clientId should be a String with length`;
            },
            get environment() {
                return `${this.ctor} needs environment String`;
            },
            get storage() {
                return `${this.ctor} needs SDK.Services.Storage instance`;
            },
            get certificateType() {
                return `${this.ctor} needs certificateKey String`;
            }
        };

        const certificateType = DrmType[certificateKey].toLowerCase();

        nonEmptyString(certificateType, errorMessage.certificateType, InvalidArgumentException);
        nonEmptyString(clientId, errorMessage.clientId, InvalidArgumentException);
        nonEmptyString(environment, errorMessage.environment, InvalidArgumentException);

        if (Check.assigned(storage)) {
            instanceStrict(storage, Storage, errorMessage.storage, InvalidArgumentException);
        }

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.certificateType = certificateType;

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.clientId = clientId;

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.environment = environment;

        /**
         *
         * @access private
         * @type {SDK.Services.Storage}
         *
         */
        this.storage = storage || new Storage(logger);

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {String}
         * @desc cache key scoped under client ID and environment to prevent clashes,
         * maintains the same structure as all other cacheKey(s) in the SDK
         *
         */
        this.cacheKey = `
            __bam_sdk_drm-${this.certificateType}-certificate--${this.clientId}_${this.environment}
        `.replace(/(?:\n\s+)/g, '');

    }

    /**
     *
     * @access private
     * @desc gets the stored certificate if found
     * @returns {Promise<Array<Byte>|Undefined>}
     *
     */
    getStoredCertificate() {

        const { cacheKey, storage } = this;

        return storage.get(cacheKey).then((storedCertificate) => {

            let certificate;

            try {

                // needs to be explictly converted to Uint8Array from an Object
                // after being parsed by storage.get
                certificate = new Uint8Array(
                    Object.keys(storedCertificate).map((byte) => storedCertificate[byte])
                );

            } catch (ex) {

                // bam-hls expects undefined in this case, note
                // that this might need to be revisited for other types
                certificate = undefined;

            }

            return Promise.resolve(certificate);

        });

    }

    /**
     *
     * @access private
     * @param {ArrayBuffer} certificate
     * @desc attempts to store a certificate via the Storage API
     * @returns {Promise<Void>}
     *
     */
    storeCertificate(certificate) {

        const { cacheKey, storage } = this;

        return storage.set(cacheKey, new Uint8Array(certificate));

    }

}



// WEBPACK FOOTER //
// ./src/drm/certificateStorage.js